import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import LifeRings from "./LifeRings";
import {
  AXIS_META,
  CARD_ORDER,
  axisHelper,
  axisLabel,
  sourceExplanation,
  stateHelp,
  stateLabel,
  windowLabel,
} from "./axisMeta";
import { AxisKey, LifeGPSContext, LifeGPSData } from "./types";

const ENERGY_PHRASES = ["inner", "outer", "balanced"];

type Selection = AxisKey | "phase" | null;

type Props = {
  data: LifeGPSData;
  updating: boolean;
  onAsk: (context: LifeGPSContext, question?: string) => void;
};

// title → phase → summary → rings → legend → cards → next step → why → ask.
const LifeGPSCard: React.FC<Props> = ({ data, updating, onAsk }) => {
  const { t } = useTranslation();
  const { currentTheme, interpretation, nextStep, reasoning } = data;
  const axes = data.lifeGps.axes;

  const [selected, setSelected] = useState<Selection>(null);
  const [hovered, setHovered] = useState<AxisKey | null>(null);
  const [sourcesOpen, setSourcesOpen] = useState(false);

  // The ring (and matching card) in focus: hover wins over a pinned selection.
  const active: AxisKey | null =
    hovered ?? (selected && selected !== "phase" ? selected : null);

  // Selection stays until the user taps somewhere else.
  useEffect(() => {
    if (!selected) return;
    const clear = (e: PointerEvent) => {
      if (!(e.target as Element | null)?.closest("[data-lgps-keep]")) {
        setSelected(null);
      }
    };
    document.addEventListener("pointerdown", clear);
    return () => document.removeEventListener("pointerdown", clear);
  }, [selected]);

  // Clicking a ring or legend item highlights its card below; nothing else
  // opens (the hover tooltip is the only popup).
  const toggle = (key: AxisKey | "phase") => {
    if (key === "phase") return;
    const next = selected === key ? null : key;
    setSelected(next);
    if (next) {
      document
        .getElementById(`lgps-card-${next}`)
        ?.scrollIntoView({ block: "nearest", behavior: "smooth" });
    }
  };

  const insightFor = (key: AxisKey | "phase") =>
    key === "phase"
      ? { title: currentTheme.label, body: interpretation.oneSentence }
      : {
          title: `${axisLabel(key)} · ${stateLabel(key, axes[key].state)}`,
          body: axes[key].meaning,
        };

  const coreTitle = t("lifeGps.card.coreTitle", {
    direction: stateLabel("direction", axes.direction.state),
    pace: stateLabel("pace", axes.pace.state),
  });
  const coreSub = windowLabel(currentTheme.window);

  const context: LifeGPSContext = {
    theme: currentTheme.label,
    oneSentence: interpretation.oneSentence,
    states: {
      direction: axes.direction.state,
      pace: axes.pace.state,
      energy: axes.energy.state,
      focus: axes.focus.state,
    },
    nextStep: nextStep.action,
  };

  const prompts = [
    t("lifeGps.prompt.energy", {
      phrase: t(
        `lifeGps.energyPhrase.${ENERGY_PHRASES.includes(axes.energy.state) ? axes.energy.state : "other"}`,
      ),
    }),
    t("lifeGps.prompt.priority"),
    t("lifeGps.prompt.shift"),
    t("lifeGps.prompt.relationships"),
    t("lifeGps.prompt.avoid"),
  ];

  // Built here (not from the backend's English line) so it follows the UI language.
  const shortSources = reasoning.sources
    .filter((s) => sourceExplanation(s))
    .map((s) => t(`lifeGps.sourceShort.${s}`));
  const memoryLine = shortSources.length
    ? t("lifeGps.personalizedWith", { sources: shortSources.join(" · ") })
    : t("lifeGps.memoryFallback");

  return (
    <section className="rounded-3xl border border-white/10 bg-[#1A131F] shadow-[0_18px_40px_rgba(0,0,0,0.45)] p-4 sm:p-6 md:p-8">
      <header className="text-center">
        <h4 className="text-xl sm:text-2xl md:text-3xl font-semibold text-slate-900 tracking-tight">
          {t("lifeGps.card.title")}
        </h4>
        <p className="mt-1 text-sm font-medium text-[#A78BFF]">
          {currentTheme.label}
        </p>
        <p className="one-sentence mx-auto mt-4 mb-7 max-w-[680px] text-base sm:text-lg text-slate-800 leading-snug">
          {interpretation.oneSentence}
        </p>
      </header>

      <details className="mx-auto -mt-3 mb-6 max-w-[680px] rounded-2xl bg-slate-50 px-4 py-3 text-sm text-slate-600">
        <summary className="cursor-pointer font-semibold text-slate-700">
          {t("lifeGps.card.whatIs")}
        </summary>
        <p className="mt-2">{t("lifeGps.card.whatIsBody")}</p>
        <ul className="mt-2 list-disc space-y-1 pl-5">
          <li><b>{axisLabel("direction")}</b>: {t("lifeGps.card.bulletDirection")}</li>
          <li><b>{axisLabel("pace")}</b>: {t("lifeGps.card.bulletPace")}</li>
          <li><b>{axisLabel("energy")}</b>: {t("lifeGps.card.bulletEnergy")}</li>
          <li><b>{axisLabel("focus")}</b>: {t("lifeGps.card.bulletFocus")}</li>
        </ul>
        <p className="mt-2">{t("lifeGps.card.whatIsNote")}</p>
      </details>

      <p className="sr-only">
        {t("lifeGps.card.srSummary", {
          direction: stateLabel("direction", axes.direction.state),
          pace: stateLabel("pace", axes.pace.state),
          energy: stateLabel("energy", axes.energy.state),
          focus: stateLabel("focus", axes.focus.state),
        })}
      </p>

      <div data-lgps-keep className="four-signal-visual">
        <LifeRings
          axes={axes}
          coreTitle={coreTitle}
          coreSub={coreSub}
          active={active}
          coreSelected={selected === "phase"}
          updating={updating}
          tooltipFor={insightFor as (a: AxisKey) => { title: string; body: string }}
          onHover={setHovered}
          onSelect={toggle}
        />

        {/* legend: 2x2 on mobile (label + value), one row on desktop */}
        <div className="mx-auto mt-3 grid max-w-[560px] grid-cols-2 gap-2 lg:flex lg:max-w-none lg:justify-center lg:gap-3">
          {CARD_ORDER.map((axis) => {
            const meta = AXIS_META[axis];
            return (
              <button
                key={axis}
                type="button"
                aria-pressed={selected === axis}
                onClick={() => toggle(axis)}
                className={`flex min-h-[44px] items-center gap-2 rounded-xl border px-3 py-2 text-left transition ${
                  selected === axis
                    ? "border-slate-300 bg-slate-50"
                    : "border-transparent hover:bg-slate-50"
                }`}
              >
                <span
                  className="h-2.5 w-2.5 shrink-0 rounded-full"
                  style={{ background: meta.ring }}
                />
                <span className="leading-tight">
                  <span
                    className="block text-xs font-bold"
                    style={{ color: meta.text }}
                  >
                    {axisLabel(axis)}
                    <span className="font-semibold text-slate-700 lg:hidden">
                      {" "}
                      · {stateLabel(axis, axes[axis].state)}
                    </span>
                  </span>
                  <span className="hidden text-[11px] text-slate-500 lg:block">
                    {axisHelper(axis)}
                  </span>
                </span>
              </button>
            );
          })}
        </div>
      </div>

      <div className="signal-meanings mt-6 grid grid-cols-1 gap-3 sm:grid-cols-2">
        {CARD_ORDER.map((axis) => {
          const meta = AXIS_META[axis];
          const isActive = active === axis;
          const state = stateLabel(axis, axes[axis].state);
          const help = stateHelp(axis, axes[axis].state);
          return (
            <div
              key={axis}
              id={`lgps-card-${axis}`}
              className="rounded-2xl border bg-white p-4 transition-shadow"
              style={{
                borderColor: isActive ? meta.ring : "rgba(255,255,255,0.08)",
                borderTop: `3px solid ${meta.ring}`,
                boxShadow: isActive ? `0 0 0 2px ${meta.ring}55` : undefined,
              }}
            >
              <div className="flex items-center justify-between gap-2">
                <strong className="text-sm" style={{ color: meta.text }}>
                  {axisLabel(axis)}
                </strong>
                <span className="rounded-full bg-slate-100 px-2.5 py-0.5 text-[11px] font-medium text-slate-700">
                  {state}
                </span>
              </div>
              <p className="mt-0.5 hidden text-[11px] text-slate-500 sm:block">
                {axisHelper(axis)}
              </p>
              {help && (
                <p className="mt-2 text-xs font-medium" style={{ color: meta.text }}>
                  {t("lifeGps.card.stateMeans", { state })} {help}
                </p>
              )}
              <p className="mt-2 text-sm leading-relaxed text-slate-700">
                {axes[axis].meaning}
              </p>
            </div>
          );
        })}
      </div>

      <div
        className="next-step mt-5 rounded-2xl p-4 sm:p-5 text-white"
        style={{
          background: "var(--astria-surface)",
          border: "1px solid rgba(167,139,255,0.25)",
          boxShadow: "0 0 32px var(--astria-accent-glow)",
        }}
      >
        <span className="text-xs font-semibold uppercase tracking-wide text-slate-300">
          {t("lifeGps.card.nextStep")}
        </span>
        <p className="mt-1 text-sm sm:text-base">{nextStep.action}</p>
        {nextStep.timeHint && (
          <p className="mt-2 text-xs text-slate-300">{nextStep.timeHint}</p>
        )}
      </div>

      <div className="why mt-5 rounded-2xl border border-slate-100 bg-white p-4">
        <span className="text-xs font-semibold uppercase tracking-wide text-slate-500">
          {t("lifeGps.card.why")}
        </span>
        {reasoning.summary && (
          <p className="mt-1 text-sm text-slate-600">{reasoning.summary}</p>
        )}
        <button
          type="button"
          aria-expanded={sourcesOpen}
          onClick={() => setSourcesOpen((o) => !o)}
          className="memory-indicator mt-3 text-left text-xs text-slate-500 underline decoration-dotted underline-offset-4 hover:text-slate-700"
        >
          {memoryLine}
        </button>
        {sourcesOpen && (
          <ul className="mt-2 space-y-1 rounded-xl bg-slate-50 p-3 text-xs text-slate-600">
            {reasoning.sources
              .filter((s) => sourceExplanation(s))
              .map((s) => (
                <li key={s}>{sourceExplanation(s)}</li>
              ))}
          </ul>
        )}
      </div>

      <div className="mt-6 text-center">
        <button
          type="button"
          onClick={() => onAsk(context)}
          className="rounded-full bg-slate-900 px-8 py-3 text-sm font-semibold text-white transition hover:bg-slate-700"
        >
          {t("lifeGps.card.ask")}
        </button>
        <div className="mt-4 flex flex-wrap justify-center gap-2">
          {prompts.map((q) => (
            <button
              key={q}
              type="button"
              onClick={() => onAsk(context, q)}
              className="rounded-full border border-slate-200 bg-white px-3 py-2 text-xs text-slate-600 transition hover:border-[#A78BFF]/40 hover:text-[#A78BFF]"
            >
              {q}
            </button>
          ))}
        </div>
      </div>
    </section>
  );
};

export default LifeGPSCard;
